import React, { useState } from 'react';
import PropTypes from 'prop-types';

import classNames from 'classnames';
import css from './TableCellTags.css';
import Tag from '../atoms/Tag';
import CellLoader from './CellLoader';
import { Icon } from './index';
import { IconSizes, IconVariants } from '../foundations/Icon';
import TagsPopover from './TagsPopover';

const getTagIcon = ({ iconName, onClick = () => {}, variant }) => (
    <Icon name={iconName} size={IconSizes.X_SMALL} variant={variant} className={css.tagActionIcon} onClick={onClick} />
);

const getDeleteIcon = (dimensionName, tag, onDeleteTag, isPlaceHolder, isEditMode) => {
    if (!isEditMode || isPlaceHolder) return null;

    return getTagIcon({
        iconName: 'x',
        onClick: e => {
            e.stopPropagation();
            onDeleteTag(dimensionName, tag);
        },
        variant: IconVariants.DARK,
    });
};

const getAddIcon = () => {
    return getTagIcon({ iconName: 'plus', variant: IconVariants.LIGHT });
};

const renderAddTagButton = (dimensionName, onAddTag, tagClassName) => (
    <Tag
        value="Add Tag"
        color="transparent"
        isFadingBackground={false}
        className={classNames(tagClassName, css.addNewTag)}
        onClick={onAddTag}
        leftIcon={getAddIcon()}
    />
);

const TableCellTags = ({
    value: tags = [],
    defaultTag = '',
    defaultColor = '',
    className = '',
    tagClassName,
    color = null,
    dimensionName = '',
    isLoading = false,
    loaderClass,
    isEditMode = false,
    onDeleteTag,
    onAddTag,
    appId,
}) => {
    const [popoverAnchor, setPopoverAnchor] = useState(null);
    const handleAddTagClick = event => {
        event.stopPropagation();
        setPopoverAnchor(event.currentTarget);
    };

    const handleClosePopover = () => {
        setPopoverAnchor(null);
    };

    const handleTagSelect = selectedTag => {
        if (selectedTag && onAddTag) {
            onAddTag(dimensionName, selectedTag);
        }
        handleClosePopover();
    };

    const renderTag = (tag, tagColor, isPlaceHolder = false) => {
        return (
            <Tag
                key={`${dimensionName}-${tag}`}
                value={tag}
                color={tagColor}
                className={tagClassName}
                rightIcon={getDeleteIcon(dimensionName, tag, onDeleteTag, isPlaceHolder, isEditMode)}
            />
        );
    };

    return (
        <>
            <td key={tags.join('_')} className={classNames(className, css.tagsContainer, { [css.loading]: isLoading })}>
                <>
                    {isLoading ? (
                        <CellLoader loaderClass={loaderClass} />
                    ) : tags.length ? (
                        tags.map(tag => renderTag(tag, color))
                    ) : defaultTag ? (
                        renderTag(defaultTag, defaultColor || color, true)
                    ) : null}
                    {isEditMode &&
                        renderAddTagButton(
                            dimensionName,
                            event => {
                                handleAddTagClick(event);
                            },
                            tagClassName
                        )}
                </>
            </td>
            <TagsPopover
                anchorElement={popoverAnchor}
                isOpen={Boolean(popoverAnchor)}
                onClose={handleClosePopover}
                onSelectTag={handleTagSelect}
                existingTags={tags}
                appId={appId}
                dimensionName={dimensionName}
            />
        </>
    );
};

TableCellTags.propTypes = {
    value: PropTypes.arrayOf(PropTypes.string),
    defaultTag: PropTypes.string,
    defaultColor: PropTypes.string,
    className: PropTypes.string,
    tagClassName: PropTypes.string,
    color: PropTypes.string,
    dimensionName: PropTypes.string,
    isLoading: PropTypes.bool,
    loaderClass: PropTypes.string,
    isEditMode: PropTypes.bool,
    onDeleteTag: PropTypes.func,
    onAddTag: PropTypes.func,
    appId: PropTypes.string,
};

export default TableCellTags;
